import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import Home from "./pages/Home";
import Subjects from "./pages/Subjects";
import Dashboard from "./pages/Dashboard";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";
import SubjectStudy from "./pages/SubjectStudy";
import SubjectNotes from "./pages/SubjectNotes";
import UnitNotes from "./pages/UnitNotes";
import PracticeSetup from "./pages/PracticeSetup";
import PracticeTest from "./pages/PracticeTest";
import PracticeSummary from "./pages/PracticeSummary";
import PersonalizedLearning from "./pages/PersonalizedLearning";
import About from "./pages/About";
import FlaggedQuestions from "./pages/FlaggedQuestions";
import TermsPrivacy from "./pages/TermsPrivacy";
import { Navigation } from "@/components/ui/navigation";
import { useAuth } from "@/hooks/useAuth";
import ReportIssue from "./pages/ReportIssue";
import Sources from "./pages/Sources";

/** Tracks page views on route changes */
function PageViewTracker() {
  const location = useLocation();
  const posthog = usePostHog();

  useEffect(() => {
    if (posthog) {
      posthog.capture("$pageview", {
        $current_url: window.location.href,
      });
    }
  }, [location.pathname, location.search, posthog]);

  return null;
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { session, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <span className="text-muted-foreground">Checking your session…</span>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/auth" replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <PageViewTracker />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route
            path="/subjects"
            element={(
              <RequireAuth>
                <Subjects />
              </RequireAuth>
            )}
          />
          <Route
            path="/subjects/:slug"
            element={(
              <RequireAuth>
                <SubjectStudy />
              </RequireAuth>
            )}
          />
          <Route
            path="/subjects/:slug/personalized"
            element={(
              <RequireAuth>
                <PersonalizedLearning />
              </RequireAuth>
            )}
          />
          <Route
            path="/subjects/:slug/notes"
            element={(
              <RequireAuth>
                <SubjectNotes />
              </RequireAuth>
            )}
          />
          <Route
            path="/subjects/:slug/notes/:unitId"
            element={(
              <RequireAuth>
                <UnitNotes />
              </RequireAuth>
            )}
          />
          <Route
            path="/practice/:slug/setup"
            element={(
              <RequireAuth>
                <PracticeSetup />
              </RequireAuth>
            )}
          />
          <Route
            path="/practice/:slug/test"
            element={(
              <RequireAuth>
                <PracticeTest />
              </RequireAuth>
            )}
          />
          <Route
            path="/practice/:slug/summary"
            element={(
              <RequireAuth>
                <PracticeSummary />
              </RequireAuth>
            )}
          />
          <Route
            path="/dashboard"
            element={(
              <RequireAuth>
                <Dashboard />
              </RequireAuth>
            )}
          />
          <Route
            path="/flagged/:slug"
            element={(
              <RequireAuth>
                <FlaggedQuestions />
              </RequireAuth>
            )}
          />
          <Route path="/auth" element={<Auth />} />
          <Route path="/about" element={<About />} />
          <Route path="/terms-privacy" element={<TermsPrivacy />} />
          {/* Redirect old routes to new merged page */}
          <Route path="/terms" element={<Navigate to="/terms-privacy" replace />} />
          <Route path="/privacy" element={<Navigate to="/terms-privacy" replace />} />
          <Route path="/report-issue" element={<ReportIssue />} />
          <Route path="/sources" element={<Sources />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
